import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { ImageFolder } from "@shared/api";

function formatLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("sk-SK", {
    timeZone: "Europe/Bratislava",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || "";
  return `${get("year")}.${get("month")}.${get("day")} ${get("hour")}:${get("minute")}`;
}

type Kind = "facebook" | "instagram";

export function SocialUploadStatusBadge({
  kind,
  folder,
}: {
  kind: Kind;
  folder: ImageFolder;
}) {
  const { t } = useTranslation();
  const status =
    kind === "facebook" ? folder.fb_upload_status ?? null : folder.ig_upload_status ?? null;
  const uploadedAt =
    kind === "facebook" ? folder.fb_uploaded_at ?? null : folder.ig_uploaded_at ?? null;
  const error =
    kind === "facebook" ? folder.fb_upload_error ?? null : folder.ig_upload_error ?? null;
  const urls = kind === "facebook" ? folder.fb_post_urls ?? null : null;
  const permalink = kind === "instagram" ? folder.ig_permalink ?? null : null;

  const isNone = !status || status === "pending" || status === "skipped";
  const label = (() => {
    if (status === "posted") {
      return folder.social_status_source === "manual"
        ? t("imagesSocial.uploadedManual")
        : t("imagesSocial.uploaded");
    }
    if (status === "partial") return t("imagesSocial.partial");
    if (status === "failed") return t("imagesSocial.failed");
    if (status === "skipped") return t("imagesSocial.notYetSkipped");
    return t("imagesSocial.notYet");
  })();

  const badgeClass = cn(
    "inline-flex max-w-full flex-col gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium",
    status === "posted" && "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200",
    status === "partial" && "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
    status === "failed" && "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200",
    isNone && "bg-muted text-muted-foreground",
  );

  const title =
    status === "skipped"
      ? t("imagesSocial.skippedTooltip")
      : status === "failed" || status === "partial"
        ? error || undefined
        : undefined;

  return (
    <div className={badgeClass} title={title || undefined} onClick={(e) => e.stopPropagation()}>
      <span>{label}</span>
      {status === "posted" || status === "partial" ? (
        <>
          {uploadedAt ? (
            <span className="font-normal opacity-80">{formatLocal(uploadedAt)}</span>
          ) : null}
          {kind === "facebook" && urls ? (
            <span className="flex flex-wrap gap-1 font-normal">
              {urls.brany ? (
                <a
                  href={urls.brany}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {t("imagesSocial.linkBrany")}
                </a>
              ) : null}
              {urls.kapuk ? (
                <a
                  href={urls.kapuk}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {t("imagesSocial.linkKapuk")}
                </a>
              ) : null}
            </span>
          ) : null}
          {kind === "instagram" && permalink ? (
            <a
              href={permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="font-normal underline"
            >
              {t("imagesSocial.linkIg")}
            </a>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
